import catalog from '../fixtures/emoji-catalog.json';

/**
 * Emoji-Eingabe (Marcel 21.10.2026) – reine Regeln, Parität zur App (emoji_helper.dart):
 * Katalog aus der gemeinsamen Fixture, Einfügen an der Cursor-Position, „Zuletzt“-Liste,
 * Enter-Verhalten (Desktop: Enter sendet, Shift+Enter Absatz; Touch: Enter macht Absatz).
 */
export interface EmojiCategory { key: string; label: string; labelEn: string; icon: string; emojis: string[] }

/** PURE: Kategorie-Name je Sprache (DE Standard, EN bei 'en') */
export const emojiCategoryLabel = (c: EmojiCategory, lang: string): string => (lang === 'en' ? c.labelEn : c.label);

export const EMOJI_RECENT_KEY = 'recent';
export const EMOJI_RECENT_STORAGE_KEY = 'dc-emoji-recent';
export const EMOJI_RECENT_MAX = 24;

export const EMOJI_CATEGORIES: EmojiCategory[] = (catalog as { categories: EmojiCategory[] }).categories;

export const emojiCount = (): number => EMOJI_CATEGORIES.reduce((n, c) => n + c.emojis.length, 0);

/** PURE: Emoji an der Auswahl einfügen (ersetzt Markierung), Cursor dahinter. */
export function insertEmoji(text: string, selectionStart: number | null, selectionEnd: number | null, emoji: string): { text: string; cursor: number } {
  const len = text.length;
  const start = selectionStart == null || selectionStart < 0 || selectionStart > len ? len : selectionStart;
  const end = selectionEnd == null || selectionEnd < start || selectionEnd > len ? start : selectionEnd;
  const next = text.slice(0, start) + emoji + text.slice(end);
  return { text: next, cursor: start + emoji.length };
}

/** PURE: „Zuletzt“ – neuestes vorn, keine Dubletten, max. 24 */
export function pushRecentEmoji(recent: string[], emoji: string): string[] {
  const out = [emoji, ...recent.filter((e) => e !== emoji)];
  return out.length > EMOJI_RECENT_MAX ? out.slice(0, EMOJI_RECENT_MAX) : out;
}

export function loadRecentEmojis(): string[] {
  try {
    const raw = window.localStorage.getItem(EMOJI_RECENT_STORAGE_KEY);
    const parsed = raw ? JSON.parse(raw) : [];
    return Array.isArray(parsed) ? parsed.filter((e): e is string => typeof e === 'string') : [];
  } catch { return []; }
}

export function rememberRecentEmoji(emoji: string): void {
  try { window.localStorage.setItem(EMOJI_RECENT_STORAGE_KEY, JSON.stringify(pushRecentEmoji(loadRecentEmojis(), emoji))); } catch { /* privater Modus */ }
}

/** Kategorien für den Picker: „Zuletzt“ nur mit Einträgen */
export function emojiCategoriesForPicker(recent: string[]): EmojiCategory[] {
  return EMOJI_CATEGORIES.flatMap((c) => (c.key === EMOJI_RECENT_KEY ? (recent.length ? [{ ...c, emojis: recent }] : []) : [c]));
}

/** Touch-Gerät (keine Umschalttaste): Enter = Absatz, Senden nur über den Knopf */
export function isTouchDevice(): boolean {
  try { return typeof window !== 'undefined' && window.matchMedia?.('(pointer: coarse)')?.matches === true; } catch { return false; }
}

/** PURE: soll Enter senden? Desktop: Enter ohne Shift sendet; Touch: nie (Enter = Absatz) */
export function shouldSendOnEnter(e: { key: string; shiftKey: boolean; isComposing?: boolean }, touch: boolean): boolean {
  if (e.key !== 'Enter' || e.shiftKey || e.isComposing) return false;
  return !touch;
}
